import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  getTelephonyProviders,
  getTelephonyProvider,
  getTelephonyProviderBusinesses,
  updateTelephonyProvider,
  manualSyncTelephonyProvider,
  getTelephonyProviderSyncLogs,
  updateTelephonyProviderMapping
} from './api';
import {
  TelephonyProviderResponse,
  TelephonyProviderBusinessesParams,
  TelephonyProviderBusinessesResponse
} from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { queryClient } from 'providers/AllProviders';

export function useGetTelephonyProviders(
  params?: any,
  options?: Omit<
    UseQueryOptions<{ data: TelephonyProviderResponse[]; meta: MetaApiResponse }, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<{ data: TelephonyProviderResponse[]; meta: MetaApiResponse }>({
    queryKey: ['telephony_providers', params],
    queryFn: () => getTelephonyProviders(params),
    ...options
  });
}

export function useGetTelephonyProvider(
  id?: string | number,
  params?: any,
  options?: Omit<UseQueryOptions<TelephonyProviderResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TelephonyProviderResponse>({
    queryKey: ['telephony_provider', id, params],
    queryFn: () => getTelephonyProvider(id!, params),
    enabled: Boolean(id),
    ...options
  });
}

export function useUpdateTelephonyProvider(id?: string | number) {
  return useMutation({
    mutationFn: (payload: FormData | Record<string, any>) => updateTelephonyProvider(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telephony_providers'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['telephony_provider', id], refetchType: 'active' });
    }
  });
}

type TelephonyProviderBusinessesQueryOptions = Omit<
  UseQueryOptions<TelephonyProviderBusinessesResponse, Error>,
  'queryKey' | 'queryFn'
>;

export function useGetTelephonyProviderBusinesses(
  id?: string | number,
  listParams?: TelephonyProviderBusinessesParams,
  options?: TelephonyProviderBusinessesQueryOptions
) {
  return useQuery<TelephonyProviderBusinessesResponse>({
    queryKey: ['telephony_provider_businesses', id, listParams],
    queryFn: () => getTelephonyProviderBusinesses(id!, listParams),
    enabled: Boolean(id),
    ...options
  });
}

export function useManualSyncTelephonyProvider(id?: string | number) {
  return useMutation({
    mutationFn: (payload: { telephony_provider: { business_id: number }; sync_type: string }) =>
      manualSyncTelephonyProvider(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telephony_provider', id], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['telephony_provider_sync_logs', id], refetchType: 'active' });
    }
  });
}

export function useGetTelephonyProviderSyncLogs(
  id?: string | number,
  businessId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<any>({
    queryKey: ['telephony_provider_sync_logs', id, businessId],
    queryFn: () => getTelephonyProviderSyncLogs(id!, businessId),
    enabled: Boolean(id),
    ...options
  });
}

export function useUpdateTelephonyProviderMapping(id?: string | number) {
  return useMutation({
    mutationFn: (payload: { telephony_provider: { business_id: number; frequency: string } }) =>
      updateTelephonyProviderMapping(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telephony_provider', id], refetchType: 'active' });
    }
  });
}
